import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { InteractivityChecker } from '@angular/cdk/a11y';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ChatService, ChatButtonStatus } from '../../chat/chat.service';
import { FocusTrapper, FocusTrapperFactory } from '../../../components/focus/focus-trapper-factory';
import { MenuService } from '../../../services/menu/menu.service';
import { LocaleService } from '../../../services/locale/locale.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { IconComponent } from '../../../components/icon/icon.component';
import { LocaleLanguage } from '../../../services/locale/locale.domain';
import { MenuPanelItem } from '../../../domain/menu.domain';

/**
 *  Mobile menu with a hamburger and floating FAB button
 *  TODO: refactor to make FAB a separate component
 */
@Component({
  selector: 'td-wb-fab-menu',
  templateUrl: 'fab-menu.component.html',
  styleUrls: ['fab-menu.component.scss'],
  imports: [
    NgClass,
    TranslatePipe,
    IconComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FabMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  get mobileToolbarExpanded(): boolean {
    return this._mobileToolbarExpanded;
  }

  set mobileToolbarExpanded(value: boolean) {
    this._mobileToolbarExpanded = value;
    if(this._mobileToolbarExpanded) {
      this.focusTrapper = this.focusTrapperFactory.create(this.fabMenuEl.nativeElement,
        { autoFocus: true });
      this.focusTrapper.savePreviouslyFocusedElement();
      this.focusTrapper.autoFocusFirstTabbableElement();
    } else {
      this.focusTrapper.returnFocusAfterClose();
      this.focusTrapper.destroy();
    }
  }

  private static DEBOUNCE_DELAY: number = 100;
  @ViewChild('fabMenu') fabMenuEl: ElementRef;

  /**
   * Hidden - true to completely hide menu & footer, ie when site is embedded
   */
  hidden: boolean;

  utilityToolbar: Array<MenuPanelItem>;

  fabOverlapsFocusable: boolean = false;
  isChatButtonOnPage: ChatButtonStatus = 'NO_BUTTON';
  currentLanguage: LocaleLanguage = LocaleLanguage.EN;

  private _mobileToolbarExpanded: boolean = false;
  private focusTrapper: FocusTrapper;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(private menuService: MenuService,
              private interactivityChecker: InteractivityChecker,
              @Inject(DOCUMENT) private document: Document,
              private changeDetectorRef: ChangeDetectorRef,
              private focusTrapperFactory: FocusTrapperFactory,
              private chatService: ChatService,
              private localeService: LocaleService) {
    this.chatService.isChatButtonOnPage
      .pipe(takeUntilDestroyed())
      .subscribe((isChatButtonOnPage: ChatButtonStatus): void => {
        this.isChatButtonOnPage = isChatButtonOnPage;
        this.changeDetectorRef.markForCheck();
      });
    this.currentLanguage = this.localeService.getLanguage();
  }

  /**
   * This function initializes the list of menus and required variables for displaying menu
   */
  ngOnInit(): void {
    this.utilityToolbar = this.menuService.getUtilityToolbar(false);
    this.menuService.onFabMenuHiddenChange
      .pipe(takeUntil(this.destroy))
      .subscribe((value: boolean): void => {
        this.hidden = value;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterViewInit(): void {
    fromEvent<MouseEvent>(this.document, 'scroll')
      .pipe(
        debounceTime(FabMenuComponent.DEBOUNCE_DELAY),
        takeUntil(this.destroy)
      )
      .subscribe((): void => {
        this.onScroll();
        this.changeDetectorRef.detectChanges();
      });

    fromEvent<MouseEvent>(this.document, 'focusin')
      .pipe(
        takeUntil(this.destroy)
      )
      .subscribe((event: MouseEvent): void => {
        this.onFocusIn(event);
        this.changeDetectorRef.detectChanges();
      });

    fromEvent<MouseEvent>(this.document, 'focusout')
      .pipe(
        takeUntil(this.destroy)
      )
      .subscribe((): void => {
        this.onFocusOut();
        this.changeDetectorRef.detectChanges();
      });
  }

  /**
   * This function toggles expanded state of mobile toolbar
   */
  onToolbarExpandCollapse(source?: 'quick-links' | undefined): boolean {
    this.mobileToolbarExpanded = !this.mobileToolbarExpanded;
    return source !== 'quick-links';
  }

  onBackdropClicked(): void {
    this.mobileToolbarExpanded = false;
  }

  // TODO:: Remove this method once API is changed
  removeIconPrefix(iconId: string): string {
    return iconId.replace('td-wb-icon--', '');
  }

  private onFocusIn(event: MouseEvent): void {
    const target: HTMLElement = event.target as HTMLElement;
    const closest = target['closest']('.td-wb-fab-menu__container, .td-wb-modal');
    if (closest === null && this.interactivityChecker.isFocusable(target)) {
      this.fabOverlapsFocusable = this.isFabOverlapsFocusable(target);
    }
  }

  private onFocusOut(): void {
    this.fabOverlapsFocusable = false;
  }

  private onScroll(): void {
    this.fabOverlapsFocusable = false;
  }

  private isFabOverlapsFocusable(element: HTMLElement): boolean {
    if (this.fabMenuEl !== undefined) {
      const fabRect = this.fabMenuEl.nativeElement.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      return !(fabRect.right < rect.left ||
        fabRect.left > rect.right ||
        fabRect.bottom < rect.top ||
        fabRect.top > rect.bottom);
    }
    return false;
  }
}
